/*
 * @Author: Yumeng Xue
 * @Date: 2025-11-28
 * @Description:
 * Detective Dashboard Component - Modern UI Redesign
 * Features: Line filtering, cluster extraction, and cluster management
 * 
 * @FilePath: /topo-mess/src/components/DetectiveDashboard.tsx
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
    Filter, ScatterChart, GripVertical, Trash2,
    Layers, Clock, ChevronRight, Sparkles
} from 'lucide-react';
import { PAPER_UI_TERMS } from '../constants/paperTerminology';
import { Cluster } from '../types/cluster';
import { Card, CardHeader, CardContent, Button, RangeSlider, Slider, Stat, Badge, Spinner } from './ui';
import { cn } from '../lib/utils';

interface DetectiveDashboardProps {
    lineFinalScores: Float32Array | null;
    lineIds: number[];
    isQueryAnalysisPending?: boolean;
    onFilterChange?: (visibleLineIds: number[]) => void;
    onFilterComplete?: (visibleLineIds: number[]) => void;
    queryResultOpacity?: number;
    onQueryResultOpacityChange?: (opacity: number) => void;
    onHoverLine?: (lineId: number | null) => void;
    onRequestCreateCluster?: (visibleLineIds: number[]) => Promise<void>;
    clusters?: Cluster[];
    onSelectCluster?: (clusterId: string | null) => void;
    onDeleteCluster?: (clusterId: string) => void;
    width?: number;
    height?: number;
}

const DetectiveDashboard: React.FC<DetectiveDashboardProps> = ({
    lineFinalScores,
    lineIds,
    isQueryAnalysisPending = false,
    onFilterChange,
    onFilterComplete,
    queryResultOpacity = 0.5,
    onQueryResultOpacityChange,
    onHoverLine,
    onRequestCreateCluster,
    clusters = [],
    onSelectCluster,
    onDeleteCluster,
    width = 400,
    height = 800
}) => {
    // =========================================================================
    // State
    // =========================================================================
    const [filterPercentage, setFilterPercentage] = useState<[number, number]>([0, 100]);
    const [isCreatingCluster, setIsCreatingCluster] = useState(false);
    const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);

    React.useEffect(() => {
        if (lineIds.length === 0) {
            setFilterPercentage([0, 100]);
        }
    }, [lineIds.length]);

    // =========================================================================
    // Computed: Selected line scores (sorted)
    // =========================================================================
    const scoresReady = Boolean(lineFinalScores && lineFinalScores.length > 0 && lineIds.length > 0);

    const selectedScores = useMemo(() => {
        if (!scoresReady || !lineFinalScores || lineIds.length === 0) {
            return [];
        }

        const scores = lineIds.map((lineId, idx) => ({
            score: lineFinalScores[lineId] || 0,
            idx: idx
        }));

        scores.sort((a, b) => a.score - b.score);
        return scores;
    }, [scoresReady, lineFinalScores, lineIds]);

    // =========================================================================
    // Computed: Filtered line indices
    // =========================================================================
    const filteredLineIndices = useMemo(() => {
        if (lineIds.length === 0) {
            return new Set<number>();
        }

        if (!scoresReady || selectedScores.length === 0) {
            return new Set<number>(lineIds.map((_, idx) => idx));
        }

        if (filterPercentage[0] >= filterPercentage[1]) {
            return new Set<number>();
        }

        const keepCount = Math.ceil(((filterPercentage[1] - filterPercentage[0]) / 100) * selectedScores.length);
        const keepStart = Math.floor((filterPercentage[0] / 100) * selectedScores.length);

        return new Set(selectedScores.slice(keepStart, keepStart + keepCount).map(item => item.idx));
    }, [scoresReady, selectedScores, filterPercentage, lineIds]);

    // =========================================================================
    // Effect: Notify parent of filter changes
    // =========================================================================
    React.useEffect(() => {
        if (onFilterChange) {
            const visibleIds = lineIds.filter((_, idx) => filteredLineIndices.has(idx));
            onFilterChange(visibleIds);
        }
    }, [filteredLineIndices, lineIds, onFilterChange]);

    // =========================================================================
    // Handlers
    // =========================================================================
    const handleFilterChange = (value: [number, number]) => {
        setFilterPercentage(value);
    };

    const handleFilterCommit = useCallback(() => {
        if (onFilterComplete) {
            const visibleIds = lineIds.filter((_, idx) => filteredLineIndices.has(idx));
            onFilterComplete(visibleIds);
        }
    }, [filteredLineIndices, lineIds, onFilterComplete]);

    const handleOpacityChange = useCallback((value: number[]) => {
        if (onQueryResultOpacityChange) {
            onQueryResultOpacityChange(value[0]);
        }
    }, [onQueryResultOpacityChange]);

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        const visibleIds = lineIds.filter((_, idx) => filteredLineIndices.has(idx));
        e.dataTransfer.setData('application/json', JSON.stringify(visibleIds));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const data = e.dataTransfer?.getData('application/json');
        if (!data) return;

        try {
            const visibleIds = JSON.parse(data) as number[];
            if (visibleIds.length === 0) return;

            setIsCreatingCluster(true);
            if (onRequestCreateCluster) {
                await onRequestCreateCluster(visibleIds);
            }
        } catch (error) {
            console.error('Failed to create cluster:', error);
        } finally {
            setIsCreatingCluster(false);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleClusterClick = (clusterId: string) => {
        const newSelectedId = selectedClusterId === clusterId ? null : clusterId;
        setSelectedClusterId(newSelectedId);
        if (onSelectCluster) {
            onSelectCluster(newSelectedId);
        }
    };

    const handleClusterDelete = (clusterId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (onDeleteCluster) {
            onDeleteCluster(clusterId);
        }
        if (selectedClusterId === clusterId) {
            setSelectedClusterId(null);
            if (onSelectCluster) {
                onSelectCluster(null);
            }
        }
    };

    // =========================================================================
    // Render: Line Filter
    // =========================================================================
    const renderLineFilter = () => {
        if (lineIds.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-surface-100 flex items-center justify-center mb-4">
                        <ScatterChart className="w-8 h-8 text-surface-400" />
                    </div>
                    <p className="text-surface-500 text-sm">{PAPER_UI_TERMS.status.noQueryResult}</p>
                    <p className="text-surface-400 text-xs mt-1">{PAPER_UI_TERMS.status.beginWithQueryWindow}</p>
                </div>
            );
        }

        const totalLines = lineIds.length;
        const visibleLines = filteredLineIndices.size;
        const percentage = totalLines > 0 ? ((visibleLines / totalLines) * 100).toFixed(1) : '0';
        const analysisMessage = isQueryAnalysisPending
            ? PAPER_UI_TERMS.status.queryAnalysisPending
            : totalLines < 3
                ? PAPER_UI_TERMS.status.queryAnalysisMinimum
                : !scoresReady
                    ? PAPER_UI_TERMS.status.queryAnalysisUnavailable
                    : null;

        return (
            <div className="space-y-6">
                {/* Stats Row */}
                <div className="grid grid-cols-2 gap-3">
                    <Stat
                        label={PAPER_UI_TERMS.subset.retained}
                        value={visibleLines}
                        suffix={`/ ${totalLines}`}
                        icon={<Layers className="w-4 h-4" />}
                    />
                    <Stat
                        label={PAPER_UI_TERMS.subset.retainedShare}
                        value={`${percentage}%`}
                        icon={<Sparkles className="w-4 h-4" />}
                    />
                </div>

                {/* Range Slider */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-surface-600">{PAPER_UI_TERMS.subset.retainByPercentile}</span>
                    </div>
                    <RangeSlider
                        value={filterPercentage}
                        onValueChange={handleFilterChange}
                        onValueCommit={handleFilterCommit}
                        min={0}
                        max={100}
                        step={1}
                        formatValue={(v) => `${v}%`}
                        disabled={!scoresReady}
                    />
                    <div className="flex justify-between text-xs text-surface-500">
                        <span>{PAPER_UI_TERMS.subset.lowerRanked}</span>
                        <span>{PAPER_UI_TERMS.subset.higherRanked}</span>
                    </div>
                    {analysisMessage && (
                        <div className="p-3 rounded-lg bg-surface-50 border border-surface-200">
                            {isQueryAnalysisPending ? (
                                <Spinner size="sm" label={analysisMessage} />
                            ) : (
                                <p className="text-xs text-surface-600 leading-relaxed text-center">
                                    {analysisMessage}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <Slider
                    label={PAPER_UI_TERMS.subset.queryResultOpacity}
                    value={[queryResultOpacity]}
                    onValueChange={handleOpacityChange}
                    min={0.05}
                    max={1}
                    step={0.05}
                    formatValue={(value) => `${Math.round(value * 100)}%`}
                />

                {/* Drag Handle */}
                <div
                    draggable
                    onDragStart={handleDragStart}
                    className={cn(
                        "flex items-center justify-center gap-3 p-4 rounded-xl cursor-move transition-all duration-200",
                        "bg-gradient-to-r from-primary-50 to-accent-50 border-2 border-dashed border-primary-300",
                        "hover:from-primary-100 hover:to-accent-100 hover:border-primary-400 hover:shadow-glow",
                        "group"
                    )}
                >
                    <GripVertical className="w-5 h-5 text-primary-500 group-hover:scale-110 transition-transform" />
                    <span className="font-medium text-primary-700">
                        {PAPER_UI_TERMS.subset.dragToExtractSubset}
                    </span>
                    <Badge variant="primary" size="md">{visibleLines} lines</Badge>
                </div>

                {/* Tip */}
                <div className="flex items-start gap-2 p-3 rounded-lg bg-surface-50 border border-surface-200">
                    <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-surface-600 leading-relaxed">
                        Adjust the slider to refine the query result by trajectory-fidelity score percentile.
                        Drag the handle above to extract the retained lines as a subset.
                    </p>
                </div>
            </div>
        );
    };

    // =========================================================================
    // Render
    // =========================================================================
    return (
        <div
            className="h-full overflow-y-auto bg-gradient-to-b from-white to-surface-50"
            style={{ width }}
        >
            <div className="p-4 space-y-4">
                {/* Header */}
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-500 to-primary-600 flex items-center justify-center">
                        <Filter className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-surface-800">{PAPER_UI_TERMS.panels.queryResultAnalysis}</h2>
                        <p className="text-xs text-surface-500">{PAPER_UI_TERMS.status.refineQueryResult}</p>
                    </div>
                </div>

                {/* Total Lines Stat */}
                <Card variant="gradient">
                    <Stat
                        label={PAPER_UI_TERMS.status.queryResultLines}
                        value={lineIds.length}
                        icon={<ScatterChart className="w-4 h-4" />}
                    />
                </Card>

                {/* Line Filter */}
                <Card>
                    <CardHeader icon={<Filter className="w-4 h-4" />}>{PAPER_UI_TERMS.panels.queryResultRefinement}</CardHeader>
                    <CardContent>
                        {renderLineFilter()}
                    </CardContent>
                </Card>

                {/* Drop Zone */}
                <Card>
                    <CardHeader icon={<Layers className="w-4 h-4" />}>{PAPER_UI_TERMS.panels.subsetExtraction}</CardHeader>
                    <CardContent>
                        <div
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            className={cn(
                                "relative min-h-[140px] rounded-xl border-2 border-dashed transition-all duration-300",
                                "flex flex-col items-center justify-center gap-3",
                                isDragOver
                                    ? "border-primary-500 bg-primary-50 scale-[1.02]"
                                    : "border-surface-300 bg-surface-50 hover:border-surface-400"
                            )}
                        >
                            {isCreatingCluster ? (
                                <Spinner size="md" label={PAPER_UI_TERMS.subset.creatingSubset} />
                            ) : (
                                <>
                                    <div className={cn(
                                        "w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300",
                                        isDragOver ? "bg-primary-100 scale-110" : "bg-surface-100"
                                    )}>
                                        <GripVertical className={cn(
                                            "w-6 h-6 transition-colors",
                                            isDragOver ? "text-primary-600" : "text-surface-400"
                                        )} />
                                    </div>
                                    <p className={cn(
                                        "text-sm font-medium transition-colors",
                                        isDragOver ? "text-primary-700" : "text-surface-500"
                                    )}>
                                        {isDragOver ? PAPER_UI_TERMS.subset.releaseToCreateSubset : PAPER_UI_TERMS.subset.dropToExtractSubset}
                                    </p>
                                </>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Cluster List */}
                {clusters.length > 0 && (
                    <Card>
                        <CardHeader icon={<Layers className="w-4 h-4" />}>
                            {PAPER_UI_TERMS.panels.extractedSubsets}
                            <Badge variant="primary" className="ml-2">{clusters.length}</Badge>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {clusters.map((cluster) => (
                                    <div
                                        key={cluster.id}
                                        onClick={() => handleClusterClick(cluster.id)}
                                        className={cn(
                                            "flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200",
                                            "border-2",
                                            selectedClusterId === cluster.id
                                                ? "border-primary-500 bg-primary-50 shadow-glow"
                                                : "border-surface-200 bg-white hover:border-surface-300 hover:shadow-sm"
                                        )}
                                    >
                                        {/* Thumbnail */}
                                        {cluster.thumbnail && (
                                            <div className="relative w-24 h-16 rounded-lg overflow-hidden flex-shrink-0 border border-surface-200">
                                                <img
                                                    src={cluster.thumbnail}
                                                    alt={`Subset ${cluster.id.slice(0, 8)}`}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                                            </div>
                                        )}

                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-semibold text-surface-800">
                                                    {cluster.count} lines
                                                </span>
                                                {selectedClusterId === cluster.id && (
                                                    <Badge variant="primary" size="sm">{PAPER_UI_TERMS.subset.active}</Badge>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1 text-xs text-surface-500">
                                                <Clock className="w-3 h-3" />
                                                <span>{new Date(cluster.createdAt).toLocaleTimeString()}</span>
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                icon={<Trash2 className="w-4 h-4" />}
                                                onClick={(e) => handleClusterDelete(cluster.id, e)}
                                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                            />
                                            <ChevronRight className={cn(
                                                "w-4 h-4 text-surface-400 transition-transform",
                                                selectedClusterId === cluster.id && "rotate-90"
                                            )} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default DetectiveDashboard;
